import React from 'react'
import InterventionList from "./InterventionList";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

// Colors of the backs, distinct from the status colors (pending, validated, overdue)
const backColors = ['#60a5fa', '#a78bfa', '#f472b6', '#2dd4bf', '#818cf8', '#38bdf8']

export default function Calendar(props) {
    const [current, setCurrent] = React.useState(new Date())
    const [scope] = React.useState(props.scope || 'month')
    const [monthList, setML] = React.useState([])
    const [inters, setInters] = React.useState([])
    const [selected, setSelected] = React.useState([])
    const [today] = React.useState(new Date())
    const [stats, setStats] = React.useState({ total: 0, validated: 0, pending: 0 })

    React.useEffect(() => {
        if (monthList.length === 0) {
            const month = current.getMonth()
            const date = new Date(current.getFullYear(), current.getMonth(), 1)
            let days = [];
            const byWeeks = [[]]
            const interventionsToDisplay = []

            const getMonday = function (d) {
                let currentDateObj = new Date(d);
                currentDateObj.setDate(currentDateObj.getDate() - (currentDateObj.getDay() + 6) % 7);
                return currentDateObj
            }

            const getNextSunday = function (d) {
                var dayOfWeek = 0;
                var currentDateObj = new Date(d);
                currentDateObj.setDate(currentDateObj.getDate() + (dayOfWeek + 7 - currentDateObj.getDay()) % 7);
                return currentDateObj
            }

            let firstofStartweek = new Date(getMonday(date))

            while (firstofStartweek < date) {
                days.push(new Date(firstofStartweek.getFullYear(), firstofStartweek.getMonth(), firstofStartweek.getDate()));
                firstofStartweek.setDate(firstofStartweek.getDate() + 1);
            }

            while (date.getMonth() === month) {
                days.push(new Date(date));
                date.setDate(date.getDate() + 1);
            }

            const endOfEndWeek = new Date(getNextSunday(date))
            while (date <= endOfEndWeek) {
                days.push(new Date(date));
                date.setDate(date.getDate() + 1);
            }

            if (props.data) {
                props.data.forEach((intervention) => {
                    let start = new Date(intervention.date.dateStart)
                    start = new Date(start.getFullYear(), start.getMonth(), start.getDate())
                    if (days.some(el => {
                        return new Date(el).getTime() === start.getTime()
                    })) {
                        interventionsToDisplay.push(intervention)
                    }
                })
            }

            let i = 0
            for (const day of days) {
                if (day.getDay() === 1) {
                    byWeeks.push([])
                    i++
                }
                byWeeks[i].push(day)
            }

            const validatedCount = interventionsToDisplay.filter(i => i.valide).length
            setStats({
                total: interventionsToDisplay.length,
                validated: validatedCount,
                pending: interventionsToDisplay.length - validatedCount
            })

            setML(byWeeks)
            setInters(interventionsToDisplay)
        }
    }, [monthList, setML, current, setInters, props.data])

    const changeCurrentMonth = (toAdd) => {
        let year = current.getFullYear()
        let month = current.getMonth()
        let date = current.getDate()

        if (scope === "month") {
            month += toAdd
        }
        if (scope === "day") {
            date += toAdd
        }
        if (scope === "week") {
            date += toAdd * 7
        }

        setML([])
        setCurrent(new Date(year, month, date))
    }

    const startOfDay = (date) => {
        const tmpDate = new Date(date)
        return new Date(tmpDate.getFullYear(), tmpDate.getMonth(), tmpDate.getDate()).getTime()
    }

    const backCovers = (back, day) => {
        const time = startOfDay(day)
        return time >= startOfDay(back.dates.start) && time <= startOfDay(back.dates.end)
    }

    const getDayBacks = (day) => (props.backs || []).filter((back) => backCovers(back, day))

    // Line of each back (0 = bottom): overlapping backs get different lines, kept for their whole duration
    const backLanes = React.useMemo(() => {
        const lanes = {}
        const laneEnds = []
        const sorted = [...(props.backs || [])].sort((a, b) => startOfDay(a.dates.start) - startOfDay(b.dates.start))
        sorted.forEach((back) => {
            let lane = laneEnds.findIndex((end) => end < startOfDay(back.dates.start))
            if (lane === -1) lane = laneEnds.length
            laneEnds[lane] = startOfDay(back.dates.end)
            lanes[back._id] = lane
        })
        return { lanes, count: laneEnds.length }
    }, [props.backs])

    // Lines used by the backs of these days (the bottom half of the day is shared between them)
    const getLanesCount = (days) => {
        const lanes = (props.backs || [])
            .filter((back) => days.some((day) => backCovers(back, day)))
            .map((back) => backLanes.lanes[back._id])
        return lanes.length > 0 ? Math.max(...lanes) + 1 : 0
    }

    // One color per property among the backs of the displayed days
    const getBackColors = (days) => {
        const colors = {}
        ;(props.backs || [])
            .filter((back) => days.some((day) => backCovers(back, day)))
            .forEach((back) => {
                if (colors[back.property]) return
                colors[back.property] = backColors[Object.keys(colors).length % backColors.length]
            })
        return colors
    }

    // Opens the day with its interventions and its backs (with their color)
    const selectDay = (day, interventions, colors) => {
        const backs = getDayBacks(day).map((back) => ({ ...back, color: colors[back.property] }))
        if (interventions.length === 0 && backs.length === 0) return
        setSelected({
            interventions,
            backs,
            header: day.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
        })
    }

    const renderBackBands = (day, colors, lanesCount) => {
        const dayBacks = getDayBacks(day)
        if (dayBacks.length === 0) return null
        return (
            <span className="back-bands" style={{ '--lanes': lanesCount }}>
                {dayBacks.map((back) => (
                    <span
                        key={back._id}
                        className="back-band"
                        style={{ '--lane': backLanes.lanes[back._id], '--back-color': colors[back.property] }}
                        title={back.property}
                    />
                ))}
            </span>
        )
    }

    const toDay = (date) => {
        const tmpDate = new Date(date)
        return tmpDate.getFullYear() + '/' + tmpDate.getMonth() + '/' + tmpDate.getDate()
    }

    if (scope === 'week') {
        const getWeekDays = () => {
            const startOfWeek = new Date(current)
            const day = startOfWeek.getDay()
            const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1)
            startOfWeek.setDate(diff)

            const days = []
            for (let i = 0; i < 7; i++) {
                const d = new Date(startOfWeek)
                d.setDate(startOfWeek.getDate() + i)
                days.push(d)
            }
            return days
        }

        const weekDaysData = getWeekDays()
        const weekBackColors = getBackColors(weekDaysData)
        const weekLanes = getLanesCount(weekDaysData)

        const getInterventionsForDay = (day) => {
            if (!props.data) return []
            return props.data.filter(inter => {
                const interDate = new Date(inter.date.dateStart)
                return interDate.toDateString() === day.toDateString()
            })
        }

        return (
            <>
                <div className="week-view">
                    <div className="week-view__header">
                        <button className="nav-btn" onClick={() => changeCurrentMonth(-1)}>
                            <FiChevronLeft />
                        </button>
                        <span className="week-title">
                            Semaine du {weekDaysData[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                        <button className="nav-btn" onClick={() => changeCurrentMonth(1)}>
                            <FiChevronRight />
                        </button>
                    </div>
                    <div className="week-view__days">
                        {weekDaysData.map((day, index) => {
                            const dayInters = getInterventionsForDay(day)
                            const isToday = day.toDateString() === today.toDateString()
                            const hasInter = dayInters.length > 0
                            const allValidated = hasInter && dayInters.every(i => i.valide)

                            let className = 'week-view__day'
                            if (isToday) className += ' week-view__day--today'

                            return (
                                <div
                                    key={index}
                                    className={className}
                                    onClick={() => selectDay(day, dayInters, weekBackColors)}
                                >
                                    <span className="day-name">{weekDays[index]}</span>
                                    <span className="day-num">{day.getDate()}</span>
                                    {hasInter && (
                                        <span className={`day-indicator ${allValidated ? 'day-indicator--validated' : 'day-indicator--pending'}`} />
                                    )}
                                    {renderBackBands(day, weekBackColors, weekLanes)}
                                </div>
                            )
                        })}
                    </div>
                    <div className="calendar-legend">
                        <span className="calendar-legend__item calendar-legend__item--pending">En attente</span>
                        <span className="calendar-legend__item calendar-legend__item--validated">Validé</span>
                        <span className="calendar-legend__item calendar-legend__item--back">Retour propriétaire</span>
                    </div>
                </div>
                <InterventionList data={selected?.interventions} backs={selected?.backs} header={selected?.header} close={() => setSelected({})} />
            </>
        )
    }

    if (scope === 'month') {
        const monthBackColors = getBackColors(monthList.flat())

        return (
            <>
                <section id="calendar">
                    <div className="day-warper">
                        <button className="btn" onClick={() => changeCurrentMonth(-1)}>
                            <FiChevronLeft />
                        </button>
                        <div className="label">
                            {current.toLocaleString('fr-FR', { month: 'long', year: 'numeric' })}
                        </div>
                        <button className="btn" onClick={() => changeCurrentMonth(1)}>
                            <FiChevronRight />
                        </button>
                    </div>

                    <div id="day-labels">
                        {weekDays.map((day, index) => (
                            <div key={index} className="label">{day}</div>
                        ))}
                    </div>

                    {monthList.map((weeks, weekIndex) => (
                        <div key={weekIndex} className="week">
                            {weeks.map((day, dayIndex, days) => {
                                const rowLanes = getLanesCount(days)
                                const hasInter = inters.filter(el => {
                                    let start = new Date(el.date.dateStart)
                                    start = new Date(start.getFullYear(), start.getMonth(), start.getDate())
                                    return start.getTime() === new Date(day).getTime()
                                })

                                let classToAdd = "day"
                                let classLabel = toDay(today) === toDay(day) ? "date today" : "date"

                                if (current.getMonth() !== day.getMonth()) {
                                    classToAdd = "day noDate"
                                }

                                if (hasInter.length > 0) {
                                    const totalValidation = hasInter.filter(inter => inter.valide)

                                    if (totalValidation.length === hasInter.length) {
                                        classToAdd = [classToAdd, 'validated'].join(' ')
                                    } else if (day < today) {
                                        classToAdd = [classToAdd, 'error'].join(' ')
                                    } else {
                                        classToAdd = [classToAdd, 'inter'].join(' ')
                                    }

                                    return (
                                        <div
                                            key={dayIndex}
                                            className={classToAdd}
                                            onClick={() => selectDay(day, hasInter, monthBackColors)}
                                        >
                                            <span className={classLabel}>{day.getDate()}</span>
                                            {renderBackBands(day, monthBackColors, rowLanes)}
                                        </div>
                                    )
                                }

                                return (
                                    <div key={dayIndex} className={classToAdd} onClick={() => selectDay(day, [], monthBackColors)}>
                                        <span className={classLabel}>{day.getDate()}</span>
                                        {renderBackBands(day, monthBackColors, rowLanes)}
                                    </div>
                                )
                            })}
                        </div>
                    ))}

                    <div className="calendar-stats">
                        <div className="calendar-stats__card calendar-stats__card--total">
                            <div className="stat-value">{stats.total}</div>
                            <div className="stat-label">Total</div>
                        </div>
                        <div className="calendar-stats__card calendar-stats__card--validated">
                            <div className="stat-value">{stats.validated}</div>
                            <div className="stat-label">Validés</div>
                        </div>
                        <div className="calendar-stats__card calendar-stats__card--pending">
                            <div className="stat-value">{stats.pending}</div>
                            <div className="stat-label">En attente</div>
                        </div>
                    </div>

                    <div className="calendar-legend">
                        <span className="calendar-legend__item calendar-legend__item--pending">En attente</span>
                        <span className="calendar-legend__item calendar-legend__item--validated">Validé</span>
                        <span className="calendar-legend__item calendar-legend__item--overdue">En retard</span>
                        <span className="calendar-legend__item calendar-legend__item--back">Retour propriétaire</span>
                    </div>
                </section>

                <InterventionList data={selected?.interventions} backs={selected?.backs} header={selected?.header} close={() => setSelected({})} />
            </>
        )
    }

    return <p>No scope defined</p>
}
